import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import type { Device } from 'src/types/Device';
import { devices } from 'src/stores/lists/devices';
import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
import { postReq } from 'src/services/http';
import { getBatteryIconStatus } from '../virtual_helpers/virtual_icons_helpers';

const MOISTURE_ALARM_COMPONENT = 'moisture_alarm:0';

/**
 * Returns true when the device is a Shelly BLU Liquid Level Sensor ZB.
 */
const SBSN_001B = 'SBSN-001B';
export const isBluLiquidLevelSensor = (deviceType?: string) => deviceType === SBSN_001B;

/**
 * Persists that the first-open introduction wizard was shown so it does not
 * auto-open again. Shared devices skip the cloud write.
 */
export const persistLiquidLevelIntroductionSeen = async (device: Device) => {
	if (!device?.id || device.shared || device.introductionWizardSeen) {
		return;
	}

	const editedDevice: Device = { ...device, introductionWizardSeen: true };
	devices.update(editedDevice);

	try {
		await postReq('interface/device/save', {
			id: editedDevice.id,
			data: JSON.stringify(editedDevice),
		});
	} catch {
		// Keep the local flag so the wizard does not auto-open again this session.
	}
};

/**
 * Cloud/gateway status uses `moisture_alarm:0.value` as a boolean:
 * true = liquid detected, false = clear.
 */
const readDetectedState = (deviceStatus: Record<string, any>): boolean | undefined => {
	const raw = deviceStatus?.[MOISTURE_ALARM_COMPONENT]?.value;

	if (typeof raw === 'boolean') return raw;
	if (raw === 1 || raw === '1') return true;
	if (raw === 0 || raw === '0') return false;
	return undefined;
};

const isNotRunning = (deviceStatus: Record<string, any>): boolean => {
	const raw = deviceStatus?.['running:0']?.value;
	return raw === false || raw === 0 || raw === '0';
};

/**
 * Builds the main-tab status labels for the BLU Liquid Level Sensor ZB.
 * Calibrated devices report `moisture_alarm:0` plus battery; uncalibrated
 * devices report `running:0` instead of a moisture alarm.
 */
export const getLiquidLevelSensorDeviceStatus = (device: Device) => {
	const deviceStatus = devices_status.getByRealId(device?.id);
	const deviceSettings = devices_settings.getByRealId(device?.id);
	const interface_data = [];
	const _format = get(format);
	if (!device || !deviceStatus || !deviceSettings) {
		return interface_data;
	}

	const parameterName = _format('label_liquid_level', { default: 'Liquid Level' });
	const detected = readDetectedState(deviceStatus);
	if (typeof detected === 'boolean') {
		interface_data.push({
			name: 'moisture_alarm',
			icon: detected ? 'text-primary fas fa-droplet' : 'fas fa-droplet-slash',
			iconColor: detected ? '--color-primary' : '',
			value: detected
				? _format('liquid_level_detected', { default: 'Detected' })
				: _format('liquid_level_clear', { default: 'Clear' }),
			translation: parameterName,
			vcName: parameterName,
			mainLabel: true,
			main: true,
		});
	} else if (isNotRunning(deviceStatus)) {
		interface_data.push({
			name: 'moisture_alarm',
			icon: 'fas fa-droplet-slash',
			value: _format('label_not_calibrated', { default: 'Not calibrated' }),
			translation: parameterName,
			vcName: parameterName,
			mainLabel: true,
			main: true,
		});
	}

	const batteryPercent = deviceStatus?.['devicepower:0']?.battery?.percent;
	const hasBatteryPercent = typeof batteryPercent === 'number';
	const batteryIcon = hasBatteryPercent
		? getBatteryIconStatus(batteryPercent)
		: { icon: 'fas fa-battery-empty' };

	interface_data.push({
		name: 'battery',
		icon: batteryIcon.icon,
		value: hasBatteryPercent ? `${batteryPercent} %` : 'N/A',
		...(batteryIcon.iconStyle && { iconStyle: batteryIcon.iconStyle }),
		...(batteryIcon.iconColor && { iconColor: batteryIcon.iconColor }),
	});

	return interface_data;
};
